import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { API_URL } from '../../services/api';

const fmt = (n) => (parseFloat(n) || 0).toFixed(2);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

const StockDetailsModal = ({ type, onClose }) => {
  const [achats, setAchats] = useState([]);   // lots achetés (stock_matieres_premieres)
  const [mouvements, setMouvements] = useState([]); // historique mouvements
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('achats');

  useEffect(() => {
    load();
  }, [type]);

  const load = async () => {
    setLoading(true);
    try {
      // Lots achetés pour ce type (stock_matieres_premieres)
      const r = await fetch(`${API_URL}/stock-matieres/stock`);
      const data = await r.json();
      const lots = (Array.isArray(data) ? data : []).filter(
        item => item.type_matiere === type
      );
      setAchats(lots);

      // Mouvements de tous ces lots (depuis stock_tracabilite/or qui inclut bijou_id)
      // On récupère l'endpoint de traçabilité pour avoir les mouvements avec bijou
      const rtrace = await fetch(`${API_URL}/stock-tracabilite/or`);
      if (rtrace.ok) {
        const traceData = await rtrace.json();
        // Filtrer les stocks du bon type
        const matchStocks = (Array.isArray(traceData) ? traceData : []).filter(
          s => s.type_matiere === type
        );
        // Assembler tous les mouvements
        const allMvts = matchStocks.flatMap(s =>
          (s.mouvements || []).map(m => ({ ...m, stock_reference: s.reference }))
        );
        // Trier par date desc
        allMvts.sort((a, b) => new Date(b.date_mouvement) - new Date(a.date_mouvement));
        setMouvements(allMvts);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const totalAchete = achats.reduce((s, a) => s + parseFloat(a.quantite_initiale || 0), 0);
  const totalDispo  = achats.reduce((s, a) => s + parseFloat(a.quantite_disponible || 0), 0);
  const totalValeur = achats.reduce((s, a) => s + parseFloat(a.prix_achat_unitaire || 0) * parseFloat(a.quantite_initiale || 0), 0);
  const totalSorties = mouvements.filter(m => m.type_mouvement === 'SORTIE').reduce((s, m) => s + parseFloat(m.quantite || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-700 text-white p-5 flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold">📦 {type}</h2>
            <div className="flex gap-6 mt-2 text-sm text-yellow-100">
              <span>Acheté : <strong className="text-white">{fmt(totalAchete)} g</strong></span>
              <span>Disponible : <strong className="text-white">{fmt(totalDispo)} g</strong></span>
              <span>Utilisé : <strong className="text-white">{fmt(totalSorties)} g</strong></span>
              <span>Valeur stock : <strong className="text-white">{fmt(totalValeur)} DH</strong></span>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-full">
            <X size={22} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50 px-4">
          <button
            onClick={() => setTab('achats')}
            className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === 'achats' ? 'border-yellow-600 text-yellow-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            🛒 Achats ({achats.length} lots)
          </button>
          <button
            onClick={() => setTab('historique')}
            className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === 'historique' ? 'border-yellow-600 text-yellow-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            📋 Historique mouvements ({mouvements.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-12 text-gray-400">Chargement...</div>
          ) : tab === 'achats' ? (
            /* ── Onglet Achats ─────────────────────────────────────── */
            achats.length === 0 ? (
              <div className="text-center py-12 text-gray-400">Aucun achat enregistré</div>
            ) : (
              <div className="space-y-3">
                {achats.map(a => (
                  <div key={a.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                    {/* Date */}
                    <div className="text-center min-w-[80px]">
                      <div className="text-xs text-gray-400">Date achat</div>
                      <div className="font-semibold text-gray-700">{fmtDate(a.date_achat)}</div>
                    </div>
                    {/* Fournisseur */}
                    <div className="flex-1">
                      <div className="text-xs text-gray-400">Fournisseur</div>
                      <div className="font-bold text-gray-900">{a.fournisseur_nom || a.fournisseur_id || '—'}</div>
                      <div className="text-xs text-gray-500">{a.reference} {a.notes ? `· ${a.notes}` : ''}</div>
                    </div>
                    {/* Poids */}
                    <div className="text-center">
                      <div className="text-xs text-gray-400">Acheté</div>
                      <div className="text-lg font-bold text-blue-600">{fmt(a.quantite_initiale)} g</div>
                    </div>
                    {/* Prix/g */}
                    <div className="text-center">
                      <div className="text-xs text-gray-400">Prix/g</div>
                      <div className="font-bold text-gray-700">{fmt(a.prix_achat_unitaire)} DH</div>
                    </div>
                    {/* Total */}
                    <div className="text-center">
                      <div className="text-xs text-gray-400">Total payé</div>
                      <div className="text-lg font-bold text-green-700">
                        {fmt(parseFloat(a.prix_achat_unitaire || 0) * parseFloat(a.quantite_initiale || 0))} DH
                      </div>
                    </div>
                    {/* Restant */}
                    <div className="text-center">
                      <div className="text-xs text-gray-400">Restant</div>
                      <div className={`text-lg font-bold ${parseFloat(a.quantite_disponible) > 0 ? 'text-yellow-600' : 'text-gray-300'}`}>
                        {fmt(a.quantite_disponible)} g
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            /* ── Onglet Historique ─────────────────────────────────── */
            mouvements.length === 0 ? (
              <div className="text-center py-12 text-gray-400">Aucun mouvement enregistré</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Type</th>
                      <th className="px-4 py-3 text-right">Quantité</th>
                      <th className="px-4 py-3 text-left">Bijou / Référence</th>
                      <th className="px-4 py-3 text-left">Devis</th>
                      <th className="px-4 py-3 text-left">Client</th>
                      <th className="px-4 py-3 text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {mouvements.map(m => {
                      const isEntree = m.type_mouvement === 'ENTREE';
                      const isSortie = m.type_mouvement === 'SORTIE';
                      return (
                        <tr key={m.id} className={`${isSortie ? 'bg-red-50' : isEntree ? 'bg-green-50' : 'bg-blue-50'} hover:opacity-80`}>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(m.date_mouvement)}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                              isEntree ? 'bg-green-200 text-green-800' :
                              isSortie ? 'bg-red-200 text-red-800' :
                              'bg-blue-200 text-blue-800'
                            }`}>
                              {m.type_mouvement === 'ENTREE' ? '⬆ ENTRÉE' : m.type_mouvement === 'SORTIE' ? '⬇ SORTIE' : '↩ RETOUR'}
                            </span>
                          </td>
                          <td className={`px-4 py-3 text-right font-bold ${isSortie ? 'text-red-600' : 'text-green-600'}`}>
                            {isSortie ? '-' : '+'}{fmt(m.quantite)} g
                          </td>
                          <td className="px-4 py-3 font-semibold text-gray-800">
                            {m.reference_produit || m.bijou_nom || '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{m.numero_devis || '—'}</td>
                          <td className="px-4 py-3 text-gray-600">{m.client_nom || '—'}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs max-w-[200px] truncate">{m.notes || m.motif || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default StockDetailsModal;
